/**
 * Nmbrs Integration Hooks
 *
 * React Query hooks for Nmbrs SOAP API integration.
 * Story 1.1: Connection status
 * Story 1.2: Employee linking
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../../lib/api';

/**
 * Nmbrs connection status response type.
 */
export interface NmbrsConnectionStatus {
  connected: boolean;
  environment: 'sandbox' | 'production';
  companyId: number;
  lastChecked: string;
}

export interface NmbrsEmployeeLink {
  id: number;
  employeeId: number;
  employeeName: string;
  nmbrsEmployeeId: number;
  nmbrsCompanyId: string | null;
  linkedAt: string;
  linkedBy: number;
  lastSyncedAt: string | null;
  syncStatus: 'active' | 'paused' | 'error';
  createdAt: string;
  updatedAt: string;
}

interface NmbrsStatusResponse {
  data: NmbrsConnectionStatus;
}

interface NmbrsLinksResponse {
  data: NmbrsEmployeeLink[];
}

interface NmbrsLinkResponse {
  data: NmbrsEmployeeLink;
}

export interface NmbrsEmployee {
  id: number;
  employeeNumber: number;
  displayName: string;
  firstName?: string;
  lastName?: string;
  emailWork?: string;
}

interface NmbrsEmployeesResponse {
  data: NmbrsEmployee[];
}

/**
 * Hook to fetch the current Nmbrs connection status.
 * Does not auto-retry on failure (connection issues are expected).
 */
export function useNmbrsStatus() {
  return useQuery({
    queryKey: ['nmbrs', 'status'],
    queryFn: () => apiRequest<NmbrsStatusResponse>('/nmbrs/status'),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to manually test the Nmbrs connection.
 * Invalidates the status query on success.
 */
export function useTestNmbrsConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiRequest<NmbrsStatusResponse>('/nmbrs/status'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'status'] });
    },
  });
}

/**
 * Hook to fetch all linked employees.
 */
export function useLinkedEmployees() {
  return useQuery({
    queryKey: ['nmbrs', 'links'],
    queryFn: () => apiRequest<NmbrsLinksResponse>('/nmbrs/links'),
  });
}

/**
 * Hook to link an employee to Nmbrs.
 * Invalidates the links query on success.
 */
export function useLinkEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { employeeId: number; nmbrsEmployeeId: number }) =>
      apiRequest<NmbrsLinkResponse>('/nmbrs/link', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'links'] });
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'unlinked'] });
    },
  });
}

/**
 * Hook to unlink an employee from Nmbrs.
 * Invalidates the links query on success.
 */
export function useUnlinkEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeId: number) =>
      apiRequest<{ data: { success: boolean } }>(`/nmbrs/link/${employeeId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'links'] });
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'unlinked'] });
    },
  });
}

/**
 * Hook to fetch unlinked Nmbrs employees (not yet linked to HR-portal).
 * Calls the Nmbrs SOAP API — may be slow on first load.
 */
export function useUnlinkedNmbrsEmployees() {
  return useQuery({
    queryKey: ['nmbrs', 'unlinked'],
    queryFn: () => apiRequest<NmbrsEmployeesResponse>('/nmbrs/employees/unlinked'),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 min cache to avoid repeated SOAP calls
  });
}

// ============================================
// Epic nmbrs-backfill, Story 1.1: Bulk koppeling
// ============================================

export interface BulkLinkMatchResult {
  employeeId: number;
  employeeName: string;
  nmbrsEmployeeId: number;
  nmbrsDisplayName: string;
}

export interface BulkLinkAlreadyLinkedResult {
  employeeId: number;
  employeeName: string;
}

export interface BulkLinkNotFoundResult {
  employeeId: number;
  employeeName: string;
}

export interface BulkLinkAmbiguousResult {
  employeeId: number;
  employeeName: string;
  candidates: { nmbrsEmployeeId: number; displayName: string }[];
}

export interface BulkLinkReport {
  mode: 'dry_run' | 'apply';
  matched: BulkLinkMatchResult[];
  alreadyLinked: BulkLinkAlreadyLinkedResult[];
  notFound: BulkLinkNotFoundResult[];
  ambiguous: BulkLinkAmbiguousResult[];
  linked?: number;
  failed?: { employeeId: number; error: string }[];
}

interface BulkLinkResponse {
  data: BulkLinkReport;
}

/**
 * Hook for the bulk-koppel-actie (Story 1.1, CAP-1). Call with `apply: false`
 * for a dry-run report, then again with `apply: true` once the admin
 * confirms. Only invalidates the links/unlinked queries after a real apply.
 */
export function useBulkLinkEmployees() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (apply: boolean) =>
      apiRequest<BulkLinkResponse>('/nmbrs/link/bulk', {
        method: 'POST',
        body: JSON.stringify({ apply }),
      }),
    onSuccess: (result) => {
      if (result.data.mode === 'apply') {
        queryClient.invalidateQueries({ queryKey: ['nmbrs', 'links'] });
        queryClient.invalidateQueries({ queryKey: ['nmbrs', 'unlinked'] });
      }
    },
  });
}
